import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';

import AdminNavTabs from '../components/AdminNavTabs';
import './AdminDashboard.css';

const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';
const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

const centsToDollars = (cents) => {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '';
  return (n / 100).toFixed(2);
};

const dollarsToCents = (dollars) => {
  const n = Number(dollars);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
};

const toEpoch = (localValue) => {
  if (!localValue) return null;
  try {
    const d = new Date(localValue);
    const ms = d.getTime();
    if (!Number.isFinite(ms)) return null;
    return Math.floor(ms / 1000);
  } catch {
    return null;
  }
};

const fromEpoch = (epoch) => {
  const n = Number(epoch);
  if (!Number.isFinite(n) || n <= 0) return '';
  try {
    const d = new Date(n * 1000);
    const pad = (x) => String(x).padStart(2, '0');
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  } catch {
    return '';
  }
};

export default function AdminDiscounts() {
  const navigate = useNavigate();
  const statusRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [username, setUsername] = useState('');

  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const [discounts, setDiscounts] = useState([]);
  const [editingCode, setEditingCode] = useState('');

  const [createForm, setCreateForm] = useState({
    code: '',
    type: 'percent',
    percent_off: 10,
    amount_off_dollars: '5.00',
    currency: 'usd',
    allow_guest: false,
    min_subtotal_dollars: '',
    max_redemptions_total: '',
    max_redemptions_per_customer: '1',
    starts_at_local: '',
    ends_at_local: '',
  });

  const [editForm, setEditForm] = useState({
    status: 'ACTIVE',
    min_subtotal_dollars: '',
    max_redemptions_total: '',
    max_redemptions_per_customer: '',
    allow_guest: false,
    starts_at_local: '',
    ends_at_local: '',
  });

  useEffect(() => {
    if (!statusMessage && !error) return;
    try {
      window.setTimeout(() => {
        try {
          statusRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
          statusRef.current?.focus?.({ preventScroll: true });
        } catch {
          // ignore
        }
      }, 50);
    } catch {
      // ignore
    }
  }, [statusMessage, error]);

  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        const groups = session.getIdToken().payload['cognito:groups'] || [];

        if (!groups.includes('Admins')) {
          navigate('/admin/login');
          return;
        }

        setUsername(user.username || user.attributes?.email || 'Admin');
        await fetchDiscounts();
      } catch {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchDiscounts = async () => {
    if (!API_BASE_URL) return;
    try {
      setError('');
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/discounts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load discounts');

      const list = Array.isArray(data?.discounts) ? data.discounts : [];
      setDiscounts(list);
    } catch (e) {
      setError(e?.message || 'Failed to load discounts');
    }
  };

  const startEdit = (d) => {
    const code = String(d?.code || '');
    const allowGuest = Boolean(d?.allow_guest);
    setEditingCode(code);
    setEditForm({
      status: String(d?.status || 'ACTIVE').toUpperCase(),
      min_subtotal_dollars: d?.min_subtotal_cents ? centsToDollars(d.min_subtotal_cents) : '',
      max_redemptions_total: d?.max_redemptions_total ?? '',
      max_redemptions_per_customer: allowGuest ? '' : (d?.max_redemptions_per_customer ?? ''),
      allow_guest: allowGuest,
      starts_at_local: fromEpoch(d?.starts_at),
      ends_at_local: fromEpoch(d?.ends_at),
    });
  };

  const clearOnKeyDown = (e, onClear) => {
    if (e.key !== 'Backspace' && e.key !== 'Delete') return;
    e.preventDefault();
    if (typeof onClear === 'function') onClear();
  };

  const cancelEdit = () => {
    setEditingCode('');
    setEditForm({
      status: 'ACTIVE',
      min_subtotal_dollars: '',
      max_redemptions_total: '',
      max_redemptions_per_customer: '',
      allow_guest: false,
      starts_at_local: '',
      ends_at_local: '',
    });
  };

  const createDiscount = async () => {
    if (!API_BASE_URL) return;
    if (saving) return;

    const code = String(createForm.code || '').trim();
    if (!code) {
      setError('Code is required');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const payload = {
        code,
        type: createForm.type,
        currency: 'usd',
        allow_guest: Boolean(createForm.allow_guest),
        percent_off: createForm.type === 'percent' ? Number(createForm.percent_off) : undefined,
        amount_off_cents: createForm.type === 'amount' ? dollarsToCents(createForm.amount_off_dollars) : undefined,
        min_subtotal_cents: createForm.min_subtotal_dollars ? dollarsToCents(createForm.min_subtotal_dollars) : undefined,
        max_redemptions_total: createForm.max_redemptions_total !== '' ? Number(createForm.max_redemptions_total) : undefined,
        max_redemptions_per_customer: createForm.allow_guest
          ? undefined
          : (createForm.max_redemptions_per_customer !== '' ? Number(createForm.max_redemptions_per_customer) : undefined),
        starts_at: toEpoch(createForm.starts_at_local),
        ends_at: toEpoch(createForm.ends_at_local),
      };

      const res = await fetch(`${API_BASE_URL}/admin/discounts`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Create failed');

      const syncOk = Boolean(data?.stripeSync?.ok);
      const syncErr = data?.stripeSync?.error;
      if (syncOk) setStatusMessage('Discount created.');
      else if (syncErr) setStatusMessage(`Discount created. Stripe sync failed: ${syncErr}`);
      else setStatusMessage('Discount created. Stripe sync failed.');

      setCreateForm((prev) => ({ ...prev, code: '' }));
      await fetchDiscounts();
    } catch (e) {
      setError(e?.message || 'Create failed');
    } finally {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        window.scrollTo(0, 0);
      }
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    if (!API_BASE_URL) return;
    if (saving) return;
    if (!editingCode) return;

    try {
      setSaving(true);
      setError('');
      setStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const payload = {
        status: String(editForm.status || 'ACTIVE').toUpperCase(),
        min_subtotal_cents: editForm.min_subtotal_dollars ? dollarsToCents(editForm.min_subtotal_dollars) : undefined,
        max_redemptions_total: editForm.max_redemptions_total !== '' ? Number(editForm.max_redemptions_total) : undefined,
        allow_guest: Boolean(editForm.allow_guest),
        max_redemptions_per_customer: editForm.allow_guest
          ? null
          : (editForm.max_redemptions_per_customer !== '' ? Number(editForm.max_redemptions_per_customer) : undefined),
        starts_at: toEpoch(editForm.starts_at_local),
        ends_at: toEpoch(editForm.ends_at_local),
      };

      const res = await fetch(`${API_BASE_URL}/admin/discounts/${encodeURIComponent(editingCode)}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Update failed');

      const syncOk = Boolean(data?.stripeSync?.ok);
      const syncErr = data?.stripeSync?.error;
      if (syncOk) setStatusMessage('Discount updated.');
      else if (syncErr) setStatusMessage(`Discount updated. Stripe sync failed: ${syncErr}`);
      else setStatusMessage('Discount updated. Stripe sync failed.');

      cancelEdit();
      await fetchDiscounts();
    } catch (e) {
      setError(e?.message || 'Update failed');
    } finally {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        window.scrollTo(0, 0);
      }
      setSaving(false);
    }
  };

  const deleteDiscount = async (code) => {
    if (!API_BASE_URL) return;
    if (!code) return;
    if (!window.confirm(`Delete discount ${code}?`)) return;

    try {
      setSaving(true);
      setError('');
      setStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/discounts/${encodeURIComponent(code)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Delete failed');

      setStatusMessage('Discount deleted.');
      await fetchDiscounts();
    } catch (e) {
      setError(e?.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const visible = useMemo(() => {
    const list = Array.isArray(discounts) ? discounts : [];
    return list.slice().sort((a, b) => String(b?.updatedAt || '').localeCompare(String(a?.updatedAt || '')));
  }, [discounts]);

  if (loading) {
    return (
      <div className="admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading discounts...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard admin-discounts-page">
      <header className="admin-header">
        <div className="admin-header-left">
          <div className="admin-header-logo">
            <i className="fas fa-tags"></i>
            <span>{PROJECT_NAME}</span>
          </div>
          <div>
            <h1>Discounts</h1>
            <p>Discount codes</p>
          </div>
        </div>
        <div className="admin-header-right">
          <AdminNavTabs />
          <span className="admin-user">
            <i className="fas fa-user-shield"></i>
            {username || 'Admin'}
          </span>
          <button onClick={() => Auth.signOut().then(() => navigate('/admin/login'))} className="btn-logout">
            <i className="fas fa-sign-out-alt"></i>
            Logout
          </button>
        </div>
      </header>

      <div className="admin-content">
        {error ? (
          <div className="alert alert-error" ref={statusRef} tabIndex={-1} role="alert" aria-live="assertive">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        ) : null}

        {statusMessage ? (
          <div className="alert alert-success" ref={statusRef} tabIndex={-1} role="status" aria-live="polite">
            <i className="fas fa-check-circle"></i>
            {statusMessage}
          </div>
        ) : null}

        <div className="products-list-container">
          <details open>
            <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>
              Create discount code
            </summary>

            <div style={{ display: 'grid', gap: 14, marginTop: 14 }}>
              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="form-group">
                  <label>Code</label>
                  <input
                    value={createForm.code}
                    onChange={(e) => setCreateForm((p) => ({ ...p, code: e.target.value }))}
                    placeholder="WELCOME10"
                  />
                </div>
                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={createForm.type}
                    onChange={(e) => setCreateForm((p) => ({ ...p, type: e.target.value }))}
                    onKeyDown={(e) => clearOnKeyDown(e, () => setCreateForm((p) => ({ ...p, type: 'percent' })))}
                  >
                    <option value="percent">Percent off</option>
                    <option value="amount">Amount off (USD)</option>
                  </select>
                </div>
              </div>

              {createForm.type === 'percent' ? (
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div className="form-group">
                    <label>Percent off</label>
                    <input
                      type="number"
                      value={createForm.percent_off}
                      onChange={(e) => setCreateForm((p) => ({ ...p, percent_off: Number(e.target.value) }))}
                      min={1}
                      max={100}
                    />
                  </div>
                  <div className="form-group">
                    <label>Min subtotal (USD) (optional)</label>
                    <input
                      value={createForm.min_subtotal_dollars}
                      onChange={(e) => setCreateForm((p) => ({ ...p, min_subtotal_dollars: e.target.value }))}
                      placeholder="20.00"
                    />
                  </div>
                </div>
              ) : (
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div className="form-group">
                    <label>Amount off (USD)</label>
                    <input
                      value={createForm.amount_off_dollars}
                      onChange={(e) => setCreateForm((p) => ({ ...p, amount_off_dollars: e.target.value }))}
                      placeholder="5.00"
                    />
                  </div>
                  <div className="form-group">
                    <label>Min subtotal (USD) (optional)</label>
                    <input
                      value={createForm.min_subtotal_dollars}
                      onChange={(e) => setCreateForm((p) => ({ ...p, min_subtotal_dollars: e.target.value }))}
                      placeholder="20.00"
                    />
                  </div>
                </div>
              )}

              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="form-group">
                  <label>Max redemptions total (optional)</label>
                  <input
                    type="number"
                    value={createForm.max_redemptions_total}
                    onChange={(e) => setCreateForm((p) => ({ ...p, max_redemptions_total: e.target.value }))}
                    placeholder="100"
                  />
                </div>
                <div className="form-group">
                  <label>Max per customer (optional)</label>
                  <input
                    type="number"
                    value={createForm.max_redemptions_per_customer}
                    onChange={(e) => setCreateForm((p) => ({ ...p, max_redemptions_per_customer: e.target.value }))}
                    disabled={Boolean(createForm.allow_guest)}
                    placeholder={createForm.allow_guest ? 'Not applicable for guests' : '1'}
                  />
                </div>
              </div>

              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 14 }}>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(createForm.allow_guest)}
                      onChange={(e) =>
                        setCreateForm((p) => ({
                          ...p,
                          allow_guest: e.target.checked,
                          ...(e.target.checked ? { max_redemptions_per_customer: '' } : {}),
                        }))
                      }
                    />
                    Allow guests (no sign-in required)
                  </label>
                </div>
              </div>

              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="form-group">
                  <label>Start (optional)</label>
                  <input
                    type="datetime-local"
                    value={createForm.starts_at_local}
                    onChange={(e) => setCreateForm((p) => ({ ...p, starts_at_local: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>End (optional)</label>
                  <input
                    type="datetime-local"
                    value={createForm.ends_at_local}
                    onChange={(e) => setCreateForm((p) => ({ ...p, ends_at_local: e.target.value }))}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" onClick={createDiscount} disabled={saving}>
                  {saving ? 'Saving…' : 'Create discount'}
                </button>
                <button className="btn" onClick={fetchDiscounts} disabled={saving}>
                  Refresh
                </button>
              </div>
            </div>
          </details>
        </div>

        <div className="products-list-container" style={{ marginTop: 18 }}>
          <details open>
            <summary style={{ cursor: 'pointer', fontWeight: 900, fontSize: '1.2rem', color: '#4c1d95' }}>
              Existing discounts
            </summary>

            <div style={{ marginTop: 14, overflowX: 'auto' }}>
              <table className="products-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Type</th>
                    <th>Value</th>
                    <th>Status</th>
                    <th>Used</th>
                    <th>Limits</th>
                    <th>Window</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((d) => {
                    const code = String(d?.code || '');
                    const isEditing = editingCode && editingCode === code;
                    const dtype = String(d?.type || '').toLowerCase();
                    const value =
                      dtype === 'percent'
                        ? `${d?.percent_off || 0}%`
                        : `$${centsToDollars(d?.amount_off_cents || 0)}`;
                    const used = Number(d?.times_redeemed_total || 0);
                    const maxTotal = d?.max_redemptions_total;
                    const maxPer = d?.max_redemptions_per_customer;
                    const limits = `${maxTotal ? `${used}/${maxTotal}` : `${used}/∞`} total, ${maxPer ? `${maxPer}` : '∞'} per`;

                    const start = d?.starts_at ? fromEpoch(d.starts_at) : '';
                    const end = d?.ends_at ? fromEpoch(d.ends_at) : '';
                    const window = `${start ? start.replace('T', ' ') : '—'} → ${end ? end.replace('T', ' ') : '—'}`;

                    return (
                      <tr key={code}>
                        <td style={{ fontWeight: 800 }}>{code}</td>
                        <td>{dtype}</td>
                        <td>{value}</td>
                        <td>{String(d?.status || 'ACTIVE')}</td>
                        <td>{used}</td>
                        <td>{limits}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{window}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {isEditing ? (
                            <div style={{ display: 'grid', gap: 10 }}>
                              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                <div className="form-group">
                                  <label>Status</label>
                                  <select
                                    value={editForm.status}
                                    onChange={(e) => setEditForm((p) => ({ ...p, status: e.target.value }))}
                                    onKeyDown={(e) => clearOnKeyDown(e, () => setEditForm((p) => ({ ...p, status: 'ACTIVE' })))}
                                  >
                                    <option value="ACTIVE">Active</option>
                                    <option value="DISABLED">Disabled</option>
                                  </select>
                                </div>
                                <div className="form-group">
                                  <label>Min subtotal (USD)</label>
                                  <input
                                    value={editForm.min_subtotal_dollars}
                                    onChange={(e) => setEditForm((p) => ({ ...p, min_subtotal_dollars: e.target.value }))}
                                    placeholder=""
                                  />
                                </div>
                              </div>

                              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                <div className="form-group">
                                  <label>Max total</label>
                                  <input
                                    type="number"
                                    value={editForm.max_redemptions_total}
                                    onChange={(e) => setEditForm((p) => ({ ...p, max_redemptions_total: e.target.value }))}
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Max per customer</label>
                                  <input
                                    type="number"
                                    value={editForm.max_redemptions_per_customer}
                                    onChange={(e) => setEditForm((p) => ({ ...p, max_redemptions_per_customer: e.target.value }))}
                                    disabled={Boolean(editForm.allow_guest)}
                                  />
                                </div>
                              </div>

                              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
                                <div className="form-group">
                                  <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <input
                                      type="checkbox"
                                      checked={Boolean(editForm.allow_guest)}
                                      onChange={(e) =>
                                        setEditForm((p) => ({
                                          ...p,
                                          allow_guest: e.target.checked,
                                          ...(e.target.checked ? { max_redemptions_per_customer: '' } : {}),
                                        }))
                                      }
                                    />
                                    Allow guests (no sign-in required)
                                  </label>
                                </div>
                              </div>

                              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                <div className="form-group">
                                  <label>Start</label>
                                  <input
                                    type="datetime-local"
                                    value={editForm.starts_at_local}
                                    onChange={(e) => setEditForm((p) => ({ ...p, starts_at_local: e.target.value }))}
                                  />
                                </div>
                                <div className="form-group">
                                  <label>End</label>
                                  <input
                                    type="datetime-local"
                                    value={editForm.ends_at_local}
                                    onChange={(e) => setEditForm((p) => ({ ...p, ends_at_local: e.target.value }))}
                                  />
                                </div>
                              </div>

                              <div style={{ display: 'flex', gap: 10 }}>
                                <button className="btn btn-primary" onClick={saveEdit} disabled={saving}>
                                  {saving ? 'Saving…' : 'Save'}
                                </button>
                                <button className="btn" onClick={cancelEdit} disabled={saving}>
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: 10 }}>
                              <button className="btn" onClick={() => startEdit(d)} disabled={saving}>
                                Edit
                              </button>
                              <button className="btn" onClick={() => deleteDiscount(code)} disabled={saving}>
                                Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {visible.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ padding: 16, textAlign: 'center', opacity: 0.75 }}>
                        No discount codes yet.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}
